import React, { useState } from "react";
import { ContainerWrapper } from "../../../components/styles";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Table,
  TableRow,
  TableCell,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  CircularProgress,
} from "@mui/material"; // Import FormControlLabel and Checkbox
import CustomButton from "../../../components/Button";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import "react-moment";
import apiClient from "../../../utils/clients/axios";
import { handleToastMsg } from "../../../components/Toast";
import { DEPARTMENTS_TASKSHEETS, HALL_NO } from "../../../config/masterdata";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { solutionImages } from "../../../utils/solutionImages";
import { isOpenIssue } from "../../../utils/issueStatus";

function TaskSheetsReport() {
  const [formData, setFormData] = useState({
    from_date: null,
    to_date: null,
    type: "",
    ALL: false,
    PENDING: false,
    REJECTED: false,
    RESOLVED: false,
  });

  const [departments, setDepartments] = useState([]);
  const [hallNo, setHallNo] = useState([]);

  const [allDepartments] = useState(["ALL", ...DEPARTMENTS_TASKSHEETS]);

  const handleDateChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  //handle for chckbox
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;

    // Reset all checkboxes
    const newFormData = {
      ...formData,
      ALL: false,
      PENDING: false,
      REJECTED: false,
      RESOLVED: false,
    };

    // Set the selected checkbox
    newFormData[name] = checked;

    // Update the type value based on the selected checkbox
    if (checked) {
      newFormData.type = name;
    } else {
      newFormData.type = "";
    }

    setFormData(newFormData);
  };

  const [loading, setLoading] = useState(false);
  const [withoutImages, setWithoutImages] = useState(true);

  //handle submit
  const handleSubmit = async (event) => {
    event.preventDefault();

    const formatDate = (date, endOfDay = false) => {
      const adjustedDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      const formattedDate = adjustedDate.toISOString().split("T")[0];
      return endOfDay
        ? formattedDate + "T23:59:59"
        : formattedDate + "T00:00:00";
    };

    const formattedFromDate = formData.from_date
      ? formatDate(new Date(formData.from_date))
      : "";
    const formattedToDate = formData.to_date
      ? formatDate(new Date(formData.to_date), true)
      : "";

    const departmentForApi = departments?.map((item) => item).join(",") || "";
    const hallForApi = hallNo?.map((item) => item).join(",") || "";

    try {
      setLoading(true);

      const response = await apiClient.get(
        `api/quality_control/report_issue/report/?from_date=${formattedFromDate}&to_date=${formattedToDate}`,
        {
          params: {
            type: formData?.type,
            department: departmentForApi,
            hallNo: hallForApi,
          },
        }
      );

      if (response?.data?.length === 0) {
        handleToastMsg("No data available", "info");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Consolidated Data");

      // Columns per solution manager; the solved image is always the last
      const MANAGER_HEADERS = [
        "Rejected Count",
        "Solution Date",
        "Seeked Date",
        "Seek Reason",
        "Review Comment",
        "Solved Image",
      ];
      const MANAGER_COLUMNS = MANAGER_HEADERS.length + 1;

      const generateDynamicHeaders = (maxManagers) => {
        const dynamicHeaders = [];
        for (let i = 1; i <= maxManagers; i++) {
          dynamicHeaders.push(`Solution Manager ${i}`, ...MANAGER_HEADERS);
        }
        return dynamicHeaders;
      };

      // Every accept / decline with the approver's remarks, one per line
      const reviewComments = (item) => {
        const reviews = item?.reviews || [];
        if (!reviews.length) return "NA";
        return reviews
          .map(
            (review) =>
              `${review.decision === "accept" ? "Accepted" : "Declined"} (${dayjs(
                review.date
              ).format("DD-MM-YYYY, hh:mm A")}${
                review.by ? `, ${review.by}` : ""
              }): ${review.remarks}`
          )
          .join("\n");
      };

      const extraDynamicHeading = response?.data?.map(
        (item) => item?.reportingManager?.length || 0
      );
      const maximumNoOfManagers = Math.max(...extraDynamicHeading);
      const dynamicHeaders = generateDynamicHeaders(maximumNoOfManagers);

      const headers = [
        "Sl No",
        "Hall",
        "Issue Date",
        "Reported By",
        "Department",
        "Machine Name",
        "Reason",
        "Reporting Manager",
        "Employee Name",
        "Description",
        "Image",
        "Status",
        "Approved Date",
        "Emergency",
        "Overdue Days",
        ...dynamicHeaders,
      ];

      sheet.addRow(headers);

      // Column positions (1-based) of the fixed part of each row
      const IMAGE_COLUMN = headers.indexOf("Image") + 1;
      const STATUS_COLUMN = headers.indexOf("Status") + 1;
      const OVERDUE_COLUMN = headers.indexOf("Overdue Days") + 1;
      const FIXED_COLUMNS = headers.length - dynamicHeaders.length;

      // Cell highlights (Excel's standard bad / good / neutral colours)
      // Yellow with red text: open (pending / rejected) status and overdue
      // (negative) days
      const ALERT = { bg: "FFFFFF00", text: "FFC00000" };
      const GOOD = { bg: "FFC6EFCE", text: "FF006100" };
      const NEUTRAL = { bg: "FFFFEB9C", text: "FF9C5700" };
      const STATUS_STYLES = { PENDING: ALERT, REJECTED: ALERT, RESOLVED: GOOD };
      const highlight = (cell, { bg, text }, bold = true) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
        if (text) cell.font = { color: { argb: text }, bold };
      };

      // Open issues only: days from today to the earliest seek date of a
      // manager whose solution isn't accepted yet. Positive = days left,
      // negative = days past the seek date.
      const overdueDays = (record) => {
        if (!isOpenIssue(record?.status)) return null;
        const seekDates = (record?.reportingManagerSolution || [])
          .filter((item) => item?.seekedDate && item?.accepted !== true)
          .map((item) => dayjs(item.seekedDate).startOf("day"))
          .filter((date) => date.isValid());
        if (!seekDates.length) return null;
        const earliest = seekDates.reduce((min, date) =>
          date.isBefore(min) ? date : min
        );
        return earliest.diff(dayjs().startOf("day"), "day");
      };

      // Resolved issues only: when the last solution was accepted. Older
      // issues have no acceptance times; their last update stands in.
      const approvedDate = (record) => {
        if (record?.status !== "RESOLVED") return "NA";
        const acceptedDates = (record?.reportingManagerSolution || [])
          .filter((item) => item?.acceptedDate)
          .map((item) => dayjs(item.acceptedDate))
          .filter((date) => date.isValid());
        const date = acceptedDates.length
          ? acceptedDates.reduce((max, d) => (d.isAfter(max) ? d : max))
          : record?.updated_at && dayjs(record.updated_at);
        return date?.isValid() ? date.format("DD-MM-YYYY, hh:mm A") : "NA";
      };

      const isUrl = (src) =>
        typeof src === "string" &&
        (/^https?:\/\//i.test(src) || src.startsWith("/"));

      // Images are stored as URLs (/uploads/...) now; older rows may still hold
      // base64 data URIs. Returns { base64, extension } for ExcelJS or null.
      const toExcelImage = async (src) => {
        if (!src || typeof src !== "string") return null;

        let dataUri = src;
        if (isUrl(src)) {
          const res = await fetch(src);
          if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
          const blob = await res.blob();
          dataUri = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }

        const match = /^data:image\/(\w+)[^,]*;base64,/.exec(dataUri);
        if (!match) return null;
        const type = match[1].toLowerCase();
        if (!["jpeg", "jpg", "png", "gif"].includes(type)) return null;
        return {
          base64: dataUri,
          extension: type === "jpg" ? "jpeg" : type,
        };
      };

      // In-app page showing every image of one issue (TaskSheetImages)
      const galleryUrl = (record) =>
        `${window.location.origin}/task-sheet-images/${record?.id}`;

      // Without images: link to the gallery page instead of embedding
      const setImageLinkCell = (cell, count, link) => {
        cell.value = {
          text: count > 1 ? `View Images (${count})` : "View Image",
          hyperlink: link,
        };
        cell.font = { color: { argb: "FF0000FF" }, underline: true };
      };

      // With images: embed them, falling back to the link if none can be loaded
      const embedImages = async (cell, sources, col, rowIndex, link) => {
        let embedded = 0;
        for (const src of sources) {
          let image = null;
          try {
            image = await toExcelImage(src);
          } catch (err) {
            console.error("Task sheet report image error:", err);
          }
          if (!image) continue;

          const imageId = workbook.addImage(image);
          sheet.addImage(imageId, {
            tl: { col: col - 1, row: rowIndex },
            ext: { width: 100, height: 90 },
            editAs: "oneCell",
          });
          embedded++;
        }

        if (embedded === 0) {
          setImageLinkCell(cell, sources.length, link);
          return;
        }

        sheet.getColumn(col).width = 20;
        sheet.getRow(rowIndex + 1).height = 100;

        if (embedded > 1) {
          // Highlight cells holding more than one image
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFD9EAD3" },
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        }
      };

      const addDataWithTitleAndHeaders = async (record, rowIndex) => {
        const extraAddHeading = record?.reportingManagerSolution?.reduce(
          (acc, item, index) => {
            const managerNameAndSolution =
              item.name && item.solution
                ? `${item?.name} : ${item?.solution}`
                : "NA";

            const rejectedCount = item?.rejectedCount ?? "NA"; //for checking null or undefined

            //this is for showing time also in the reports for solution date only
            const managerSolutionDate = item.solutionDate
              ? dayjs(item?.solutionDate).format("DD-MM-YYYY, hh:mm A")
              : "NA";

            const managerSeekedDate = item?.seekedDate
              ? dayjs(item?.seekedDate).format("DD-MM-YYYY")
              : "NA";

            const managerSeekReason = item?.seekReason
              ? item?.seekReason
              : "NA";

            acc.push(managerNameAndSolution);
            acc.push(rejectedCount);
            acc.push(managerSolutionDate);
            acc.push(managerSeekedDate);
            acc.push(managerSeekReason);
            acc.push(reviewComments(item));
            acc.push("");

            return acc;
          },
          []
        );

        let updatedOne = [];
        if (maximumNoOfManagers > record?.reportingManagerSolution?.length) {
          const count =
            maximumNoOfManagers - record?.reportingManagerSolution?.length;

          for (let i = 0; i < count; i++) {
            updatedOne.push(...Array(MANAGER_COLUMNS).fill("-"));
          }
        }

        const managersRespondsHeading =
          maximumNoOfManagers === record?.reportingManagerSolution?.length
            ? extraAddHeading
            : [...extraAddHeading, ...updatedOne];

        const formattedDate = dayjs(record?.created_at).format(
          "DD-MM-YYYY, hh:mm A"
        );

        const rowData = [
          record?.slNo,
          record?.hall,
          formattedDate,
          record?.reportedBy,
          record?.department?.map((item) => item).join(", "),
          record?.machineName || "NA",
          record?.reason || "NA",
          record?.reportingManager?.map((item) => item?.name).join(", "),
          record?.employeeName || "NA",
          record?.description,
          "", // if there is no image
          record?.status,
          approvedDate(record),
          record?.isEmergency ? "YES" : "NO",
          "", // overdue days, filled below
          // record?.video ? record?.video : "NA", // This will temporarily assign the video link or "NA"
          ...managersRespondsHeading,
        ];

        //Add the row
        // sheet.addRow(rowData);

        //Add the row for video
        const newRow = sheet.addRow(rowData);

        // Status: red on yellow while open, green when resolved
        const statusStyle = STATUS_STYLES[record?.status];
        if (statusStyle) highlight(newRow.getCell(STATUS_COLUMN), statusStyle);

        if (record?.isEmergency) {
          newRow.getCell(headers.indexOf("Emergency") + 1).font = {
            color: { argb: ALERT.text },
            bold: true,
          };
        }

        // Solution manager columns: highlight managers with no solution yet
        // (first of each manager's columns; "-" padding cells stay plain).
        // Review comments may span several lines.
        (record?.reportingManagerSolution || []).forEach((item, i) => {
          const start = FIXED_COLUMNS + 1 + i * MANAGER_COLUMNS;
          const cell = newRow.getCell(start);
          if (cell.value === "NA") {
            highlight(cell, { bg: NEUTRAL.bg, text: "FF9C0006" }); // dark red
          }
          newRow.getCell(start + MANAGER_COLUMNS - 2).alignment = {
            wrapText: true,
            vertical: "top",
          };
        });

        // Overdue days: + days left (green), - overdue (red on yellow),
        // 0 due today (amber)
        const overdue = overdueDays(record);
        const overdueCell = newRow.getCell(OVERDUE_COLUMN);
        if (overdue === null) {
          overdueCell.value = "NA";
        } else {
          overdueCell.value = overdue;
          overdueCell.numFmt = "+0;-0;0";
          highlight(
            overdueCell,
            overdue < 0 ? ALERT : overdue > 0 ? GOOD : NEUTRAL
          );
        }

        // Check if `record?.video` is a valid link and update the cell with a hyperlink
        // if (record?.video) {
        //   const videoCell = newRow.getCell(10); // Assuming 'video' is in the 10th column

        //   videoCell.value = {
        //     text: "Click here", // Displayed text for the link
        //     hyperlink: record?.video, // The actual link
        //   };

        //   // Apply font styling to make it look like a typical hyperlink
        //   videoCell.font = {
        //     color: { argb: "FF0000FF" }, // Blue color (ARGB format)
        //     underline: true, // Underline to resemble a link
        //   };
        // }


        //--------------------------------------------//
        
        // Function to convert image URL to Base64
        // const urlToBase64 = async (url) => {
        //   const response = await fetch(url);
        //   const blob = await response.blob();

        //   return new Promise((resolve) => {
        //     const reader = new FileReader();
        //     reader.onloadend = () => resolve(reader.result);
        //     reader.readAsDataURL(blob);
        //   });
        // };

        // Manager solution images: the last of each manager's columns
        for (let i = 0; i < record?.reportingManagerSolution?.length; i++) {
          const item = record.reportingManagerSolution[i];
          const currentColumn = FIXED_COLUMNS + (i + 1) * MANAGER_COLUMNS;
          const cell = newRow.getCell(currentColumn);
          const sources = solutionImages(item);

          if (sources.length === 0) {
            cell.value = "NA";
          } else if (withoutImages) {
            setImageLinkCell(cell, sources.length, galleryUrl(record));
          } else {
            await embedImages(
              cell,
              sources,
              currentColumn,
              rowIndex,
              galleryUrl(record)
            );
          }
        }

        // Reported images
        const reportedImages = Array.isArray(record?.image)
          ? record.image.filter(Boolean)
          : [];
        const imageCell = newRow.getCell(IMAGE_COLUMN);

        if (reportedImages.length === 0) {
          imageCell.value = "No Image";
        } else if (withoutImages) {
          setImageLinkCell(
            imageCell,
            reportedImages.length,
            galleryUrl(record)
          );
        } else {
          await embedImages(
            imageCell,
            reportedImages,
            IMAGE_COLUMN,
            rowIndex,
            galleryUrl(record)
          );
        }
      };

      // Rows in Sl No ascending order
      const records = [...(response?.data || [])].sort(
        (a, b) => (Number(a?.slNo) || 0) - (Number(b?.slNo) || 0)
      );

      for (let i = 0; i < records.length; i++) {
        await addDataWithTitleAndHeaders(records[i], i + 1);
      }

      // response?.data?.forEach(async (item, index) => {
      //   return await addDataWithTitleAndHeaders(item, index + 1); // Starting from row 2 to account for the header row
      // });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "TASK-SHEETS-REPORTS.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      handleToastMsg("Reports downloaded successfully", "success");
    } catch (error) {
      handleToastMsg("Data Error Occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ContainerWrapper container rowGap={2} height={0}>
        <Grid item xs={12}>
          <h2>Task Sheets Report</h2>
        </Grid>

        <Grid item container mb={3}>
          <Grid
            item
            container
            xs={12}
            md={5}
            alignItems={"center"}
            columnGap={2}
          >
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="Date from"
                    value={formData.from_date}
                    onChange={(value) => handleDateChange("from_date", value)}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid
            item
            container
            xs={12}
            md={5}
            alignItems={"center"}
            columnGap={2}
          >
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="Date to"
                    value={formData.to_date}
                    onChange={(value) => handleDateChange("to_date", value)}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>

        <Grid item container xs={12} md={12} alignItems={"center"}>
          <Table>
            <TableRow>
              <TableCell>Select Hall</TableCell>
              <TableCell>
                <FormControl style={{ width: "200px" }}>
                  <InputLabel id="select-hall">Select Hall</InputLabel>
                  <Select
                    multiple
                    label="Select Hall"
                    value={hallNo}
                    onChange={(e) => setHallNo(e.target.value)}
                    variant="outlined"
                    renderValue={(selected) => selected.join(", ")}
                  >
                    {HALL_NO?.map((hall) => (
                      <MenuItem key={hall} value={hall}>
                        <Checkbox checked={hallNo?.indexOf(hall) > -1} />
                        <ListItemText primary={hall} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Select Departments</TableCell>
              <TableCell>
                <FormControl style={{ width: "200px" }}>
                  <InputLabel id="select-department">
                    Select Department
                  </InputLabel>
                  <Select
                    multiple
                    label="Select Department"
                    value={departments}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Check if "ALL" is selected
                      if (value.includes("ALL")) {
                        // If "ALL" is selected, set departments to ["ALL"]
                        setDepartments(["ALL"]);
                      } else {
                        // Otherwise, set the selected departments (excluding "ALL" if previously selected)
                        setDepartments(value.filter((dept) => dept !== "ALL"));
                      }
                    }}
                    variant="outlined"
                    renderValue={(selected) => selected.join(", ")}
                  >
                    {allDepartments?.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        <Checkbox checked={departments?.indexOf(dept) > -1} />
                        <ListItemText primary={dept} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Select Type</TableCell>
              <TableCell>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.ALL}
                        onChange={handleCheckboxChange}
                        name="ALL"
                      />
                    }
                    label="ALL"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.PENDING}
                        onChange={handleCheckboxChange}
                        name="PENDING"
                      />
                    }
                    label="PENDING"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.REJECTED}
                        onChange={handleCheckboxChange}
                        name="REJECTED"
                      />
                    }
                    label="REJECTED"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.RESOLVED}
                        onChange={handleCheckboxChange}
                        name="RESOLVED"
                      />
                    }
                    label="RESOLVED"
                  />
                </FormGroup>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Images</TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={withoutImages}
                      onChange={(e) => setWithoutImages(e.target.checked)}
                    />
                  }
                  label="Report without images (faster)"
                />
              </TableCell>
            </TableRow>
          </Table>
        </Grid>
        <Grid item container alignItems={"center"}></Grid>

        <Grid item container justifyContent={"flex-end"} xs={12} md={12}>
          <CustomButton
            style={{ width: "150px", height: "50px" }}
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress sx={{ color: "white" }} size={32} />
            ) : (
              "Generate"
            )}
          </CustomButton>
        </Grid>
      </ContainerWrapper>
    </form>
  );
}

export default TaskSheetsReport;
